import useI18n from 'Hooks/useI18n'
import TipsyButton from '@/components/ui-kit/TipsyButton'
import { useState } from 'react'
import CommonModal from '@/components/modal/CommonModal'
import TipsyModal from '@/components/modal/TipsyModal'
import Image from 'next/image'
import { CaretDownOutlined } from '@ant-design/icons'
import type { DeleteReasonSelection } from '@/utils/deleteAccountReason'

interface DeleteReasonSubCategory {
  id: string
  key: string
}

interface DeleteReasonCategory {
  id: string
  key: string
  subCategories: DeleteReasonSubCategory[]
}

const DELETE_REASON_CATEGORIES: DeleteReasonCategory[] = [
  {
    id: 'starting_over',
    key: 'Starting Over',
    subCategories: [
      {
        id: 'switching_accounts',
        key: 'Switching accounts (to get gems/rewards)',
      },
      {
        id: 'start_fresh',
        key: 'Start fresh (to clear all memory)',
      },
    ],
  },
  {
    id: 'chat_experience',
    key: 'Chat Experience',
    subCategories: [
      {
        id: 'repetitive_replies',
        key: 'Repetitive or robotic replies',
      },
      {
        id: 'forgets_conversations',
        key: 'Forgets our past conversations',
      },
      {
        id: 'too_slow',
        key: 'Too slow, laggy, or buggy',
      },
      {
        id: 'no_interesting_stories',
        key: 'No interesting stories/characters',
      },
      {
        id: 'lack_voice_visual',
        key: 'Lack of voice or visual features',
      },
    ],
  },
  {
    id: 'price_gems',
    key: 'Price & Gems',
    subCategories: [
      {
        id: 'too_expensive',
        key: 'Too expensive / Not enough gems',
      },
      {
        id: 'not_worth_subscription',
        key: 'Not worth the subscription',
      },
    ],
  },
  {
    id: 'privacy_concerns',
    key: 'Privacy Concerns',
    subCategories: [
      {
        id: 'data_history_leak',
        key: 'Worried about data/history leak',
      },
    ],
  },
  {
    id: 'personal_reasons',
    key: 'Personal Reasons',
    subCategories: [
      {
        id: 'life_got_busy',
        key: 'Life got busy',
      },
      {
        id: 'just_trying_out',
        key: 'Just trying it out',
      },
    ],
  },
  {
    id: 'other',
    key: 'Other',
    subCategories: [],
  },
]

type Props = {
  open: boolean
  onCancel: () => void
  onNext: (reason: DeleteReasonSelection) => void
}

export default function DeleteReasonModal({ open, onCancel, onNext }: Props) {
  const { t } = useI18n()
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string>('')
  const [feedback, setFeedback] = useState<string>('')
  const [followUpWilling, setFollowUpWilling] = useState<boolean | null>(null)
  const [followUpEmail, setFollowUpEmail] = useState<string>('')
  const [startFreshReminderVisible, setStartFreshReminderVisible] =
    useState(false)

  const isOtherSelected = selectedCategoryId === 'other'
  const isStartFreshSelected =
    selectedCategoryId === 'starting_over' &&
    selectedSubCategoryId === 'start_fresh'
  const isReasonSelected =
    selectedCategoryId && (isOtherSelected ? true : selectedSubCategoryId)
  const isFollowUpValid =
    followUpWilling !== null &&
    (followUpWilling === false || followUpEmail.trim())
  const canProceed = isReasonSelected && isFollowUpValid

  const handleCategoryClick = (categoryId: string) => {
    if (expandedCategory === categoryId) {
      // 如果已经展开，则收起
      setExpandedCategory(null)
    } else {
      // 展开当前分类
      setExpandedCategory(categoryId)
      setSelectedCategoryId(categoryId)
      // 如果切换到其他分类，清空二级选择
      if (selectedCategoryId !== categoryId) {
        setSelectedSubCategoryId('')
      }
    }
  }

  const handleSubCategoryClick = (
    categoryId: string,
    subCategoryId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation() // 阻止事件冒泡，避免触发一级分类的点击
    setSelectedCategoryId(categoryId)
    setSelectedSubCategoryId(subCategoryId)
  }

  const buildDeleteReasonSelection = (): DeleteReasonSelection => {
    return {
      categoryId: selectedCategoryId,
      subCategoryId: isOtherSelected ? '' : selectedSubCategoryId,
      suggestion: feedback.trim() || undefined,
      followUp:
        followUpWilling !== null
          ? {
              willing: followUpWilling,
              email: followUpWilling ? followUpEmail.trim() : undefined,
            }
          : undefined,
    }
  }

  const proceedToNext = () => {
    onNext(buildDeleteReasonSelection())
  }

  const handleNext = () => {
    if (!canProceed) return

    if (isStartFreshSelected) {
      setStartFreshReminderVisible(true)
      return
    }

    proceedToNext()
  }

  const handleCancel = () => {
    setStartFreshReminderVisible(false)
    setExpandedCategory(null)
    setSelectedCategoryId('')
    setSelectedSubCategoryId('')
    setFeedback('')
    setFollowUpWilling(null)
    setFollowUpEmail('')
    onCancel()
  }

  return (
    <>
      <CommonModal
        open={open}
        footer={null}
        isLoading={false}
        onCancel={handleCancel}
        width={500}
      >
        <div className="flex h-full flex-col overflow-hidden">
          <div className="mb-4 text-center text-base font-medium">
            {t('Delete Account')}
          </div>

          <div className="flex-1 overflow-y-auto pr-2">
            <div className="mb-3 text-center text-[13px] text-white/50">
              {t(
                'It hurts to see you go. Help us improve for your next visit?'
              )}
            </div>
            <div className="space-y-0 divide-y divide-white/10 rounded-[10px] bg-white/5 opacity-70 shadow-[0_0_1px_0_rgba(255,255,255,0.18)]">
              {DELETE_REASON_CATEGORIES.map((category) => (
                <div key={category.id}>
                  {/* 一级原因 */}
                  <div
                    className={`flex h-[50px] cursor-pointer items-center px-3 text-[13px] transition-colors hover:bg-white/5 ${
                      selectedCategoryId === category.id
                        ? 'font-medium text-white'
                        : 'text-white/70'
                    }`}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    <span className="flex-1">{t(category.key)}</span>
                    {category.subCategories.length > 0 ? (
                      <CaretDownOutlined
                        className={`ml-2 text-[10px] text-white/50 transition-transform duration-200 ${
                          expandedCategory === category.id ? 'rotate-180' : ''
                        }`}
                      />
                    ) : (
                      <Image
                        src={
                          selectedCategoryId === category.id
                            ? '/settings/delete/check.png'
                            : '/settings/delete/uncheck.png'
                        }
                        alt=""
                        width={24}
                        height={24}
                      />
                    )}
                  </div>

                  {/* 二级原因 */}
                  {expandedCategory === category.id &&
                    category.subCategories.length > 0 && (
                      <div className="pb-1">
                        {category.subCategories.map((sub) => (
                          <div
                            key={sub.id}
                            className="flex h-[44px] cursor-pointer items-center pl-8 pr-3 text-[13px] text-white/60 transition-colors hover:bg-white/5"
                            onClick={(e) =>
                              handleSubCategoryClick(category.id, sub.id, e)
                            }
                          >
                            <span className="flex-1">{t(sub.key)}</span>
                            <Image
                              src={
                                selectedCategoryId === category.id &&
                                selectedSubCategoryId === sub.id
                                  ? '/settings/delete/check.png'
                                  : '/settings/delete/uncheck.png'
                              }
                              alt=""
                              width={20}
                              height={20}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>

            {/* 通用反馈文本框（选填） */}
            <div className="relative mt-3">
              <textarea
                className="h-[120px] w-full resize-none rounded-[10px] bg-white/5 p-3 text-[13px] text-white opacity-70 shadow-[0_0_1px_0_rgba(255,255,255,0.18)] outline-none placeholder:italic placeholder:text-white/30"
                placeholder={t(
                  'Tell us more... What could we change to make you stay? (e.g., I want the AI to remember more, or gems are too expensive)'
                )}
                value={feedback}
                onChange={(e) => {
                  const value = e.target.value
                  if (value.length <= 500) {
                    setFeedback(value)
                  }
                }}
                maxLength={500}
              />
              <div className="absolute bottom-3 right-3 text-[11px] text-white/30">
                {feedback.length}/500
              </div>
            </div>

            {/* Follow-up interview（必填） */}
            <div className="mt-3">
              <div
                className="mb-2 text-[13px]"
                style={{ color: 'rgb(255 255 255 / 0.5)' }}
              >
                {t('Are you open to a follow-up interview?')}{' '}
                <span className="text-[#D85C55]">*</span>
              </div>
              <div className="divide-y divide-white/10 rounded-[10px] bg-white/5 opacity-70 shadow-[0_0_1px_0_rgba(255,255,255,0.18)]">
                {/* Yes */}
                <div>
                  <div
                    className="flex h-[50px] cursor-pointer items-center px-3 text-[13px] transition-colors hover:bg-white/5"
                    onClick={() => setFollowUpWilling(true)}
                  >
                    <span className="flex-1 text-white/70">{t('Yes')}</span>
                    <Image
                      src={
                        followUpWilling === true
                          ? '/settings/delete/check.png'
                          : '/settings/delete/uncheck.png'
                      }
                      alt=""
                      width={24}
                      height={24}
                    />
                  </div>
                  {followUpWilling === true && (
                    <div className="px-3 pb-3">
                      <input
                        type="email"
                        className="h-[44px] w-full rounded-lg bg-white/5 px-3 text-[13px] text-white outline-none placeholder:text-white/30"
                        placeholder="example@mail.com"
                        value={followUpEmail}
                        onChange={(e) => setFollowUpEmail(e.target.value)}
                      />
                    </div>
                  )}
                </div>
                {/* No */}
                <div
                  className="flex h-[50px] cursor-pointer items-center px-3 text-[13px] transition-colors hover:bg-white/5"
                  onClick={() => {
                    setFollowUpWilling(false)
                    setFollowUpEmail('')
                  }}
                >
                  <span className="flex-1 text-white/70">{t('No')}</span>
                  <Image
                    src={
                      followUpWilling === false
                        ? '/settings/delete/check.png'
                        : '/settings/delete/uncheck.png'
                    }
                    alt=""
                    width={24}
                    height={24}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 pr-2">
            <TipsyButton
              className={`w-full rounded-full py-3 text-white ${
                canProceed ? 'bg-[#D85C55]' : 'bg-white/20 opacity-50'
              }`}
              onClick={handleNext}
              disabled={!canProceed}
            >
              {t('Next')}
            </TipsyButton>
          </div>
        </div>
      </CommonModal>
      <TipsyModal
        open={open && startFreshReminderVisible}
        onClose={handleCancel}
        onConfirm={() => {
          setStartFreshReminderVisible(false)
          proceedToNext()
        }}
        title={t('Friendly Reminder')}
        desc={t(
          'You can Reset OC in Character Settings to clear all chats without deleting your account'
        )}
        cancelText={t('Cancel')}
        confirmText={t('Continue Deletion')}
      />
    </>
  )
}
